import React, { useEffect, useState } from "react";
import { authAPI, brokerAPI } from "../services/api";

const IS_CRYPTO_PORTAL = typeof window !== "undefined" && /^crypto\./i.test(window.location.hostname || "");

// Delta Exchange (crypto) API key card — shown in Settings on the crypto portal.
// Saves the key+secret to the server (provider:'delta' broker) and reports whether
// it validated. The key is stored even if validation fails (IP not whitelisted yet).
function DeltaApiCard() {
  const [form, setForm] = useState({ name: "Delta Exchange", apiKey: "", apiSecret: "" });
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => brokerAPI.list()
    .then(r => setExisting((r.data || []).find(b => b.provider === "delta" || /delta/i.test(b.name || "")) || null))
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const dark = IS_CRYPTO_PORTAL;
  const C = {
    card: dark ? "#181a20" : "#fff", border: dark ? "#2b3139" : "#e2e8f0",
    text: dark ? "#eaecef" : "#0f172a", sub: dark ? "#9aa3b2" : "#64748b",
    field: dark ? "#0b0e11" : "#fff", blue: dark ? "#2c8cff" : "#2563eb",
  };
  const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "22px 24px", maxWidth: 560, marginTop: 18 };
  const lbl = { fontSize: 12, fontWeight: 700, color: C.sub, marginBottom: 6, display: "block" };
  const input = { width: "100%", padding: "11px 13px", fontSize: 14, borderRadius: 9, border: `1px solid ${C.border}`, background: C.field, color: C.text, boxSizing: "border-box", outline: "none" };

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await brokerAPI.deltaConnect(form);
      setMsg({ ok: r.data.connected, text: r.data.message || (r.data.connected ? "Connected" : "Saved") });
      setForm(f => ({ ...f, apiSecret: "" }));
      load();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.message || "Could not save." }); }
    finally { setBusy(false); }
  };

  return (
    <div style={card}>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 4 }}>₿ Delta Exchange API</div>
      <div style={{ fontSize: 12.5, color: C.sub, marginBottom: 16 }}>
        Save your Delta (crypto) API key for live & paper trading. Create it at delta.exchange → Account → API Keys, enable trading, and <b>whitelist this server's IP: 88.222.214.240</b>.
      </div>
      {existing && (
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: existing.connected ? "#0ecb81" : "#f0a020" }}>
          {existing.connected ? "● Connected" : "● Saved (not validated — check IP whitelist)"} · key …{String(existing.clientId || "").slice(-6)}
        </div>
      )}
      {["name", "apiKey", "apiSecret"].map(k => (
        <div key={k} style={{ marginBottom: 12 }}>
          <label style={lbl}>{k === "apiKey" ? "API Key" : k === "apiSecret" ? "API Secret" : "Label"}</label>
          <input style={input} type={k === "apiSecret" ? "password" : "text"} value={form[k]} placeholder={k === "apiSecret" && existing ? "•••••• (leave blank to keep)" : ""} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
        </div>
      ))}
      {msg && <div style={{ marginTop: 6, marginBottom: 12, padding: "9px 13px", borderRadius: 9, fontSize: 13, fontWeight: 600, background: msg.ok ? "rgba(14,203,129,0.12)" : "rgba(240,160,32,0.12)", color: msg.ok ? "#0ecb81" : "#f0a020" }}>{msg.text}</div>}
      <button onClick={save} disabled={busy || !form.apiKey || !form.apiSecret}
        style={{ marginTop: 6, background: (busy || !form.apiKey || !form.apiSecret) ? C.sub : `linear-gradient(135deg,${C.blue},#1d6fe0)`, color: "#fff", border: "none", borderRadius: 10, padding: "11px 22px", fontSize: 14, fontWeight: 700, cursor: busy ? "wait" : "pointer" }}>
        {busy ? "Saving…" : "Save API key"}
      </button>
    </div>
  );
}

// Settings → Notifications. WhatsApp-alert opt-in: a number + a toggle. The engine
// (engine/whatsapp.js) fires an alert on EVERY activity event (entry, exit + P&L,
// SL placed/rejected, order/exit rejected, cancelled, adjust/roll, recovery, info)
// for opted-in users. Saving busts the engine's pref cache so it applies instantly.
export default function Settings() {
  const [alerts, setAlerts] = useState(false);
  const [number, setNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });

  useEffect(() => {
    authAPI.me()
      .then(r => { setAlerts(!!r.data.whatsappAlerts); setNumber(r.data.whatsappNumber || ""); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setMsg({ text: "", type: "" });
    try {
      const r = await authAPI.updateNotifications({ whatsappAlerts: alerts, whatsappNumber: number });
      setAlerts(!!r.data.whatsappAlerts); setNumber(r.data.whatsappNumber || "");
      setMsg({ text: "Saved — alerts " + (r.data.whatsappAlerts ? "ON" : "OFF") + ".", type: "success" });
    } catch (e) {
      setMsg({ text: e?.response?.data?.message || "Could not save. Try again.", type: "error" });
    } finally { setSaving(false); }
  };

  const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "22px 24px", maxWidth: 560 };
  const lbl = { fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 6, display: "block" };
  const input = { width: "100%", padding: "11px 13px", fontSize: 14, borderRadius: 9, border: "1px solid #cbd5e1", boxSizing: "border-box" };

  return (
    <div style={{ padding: "8px 4px" }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: "0 0 4px" }}>Settings</h1>
      <p style={{ fontSize: 13.5, color: "#64748b", margin: "0 0 20px" }}>WhatsApp alerts for your live trading activity.</p>

      <div style={card}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>📱 WhatsApp Alerts</div>
        <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 18 }}>
          Get a WhatsApp message on every engine event — entry, exit (with P&amp;L), SL placed/rejected, order rejections, adjust/roll, recovery and more.
        </div>

        {loading ? <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading…</div> : (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>WhatsApp number (with country code)</label>
              <input style={input} value={number} onChange={e => setNumber(e.target.value)} placeholder="+91 98765 43210" inputMode="tel" />
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 5 }}>Use the number registered on WhatsApp. Country code required (e.g. +91 for India).</div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 11, cursor: "pointer", padding: "10px 0" }}>
              <span onClick={() => setAlerts(a => !a)} style={{ width: 42, height: 24, borderRadius: 24, background: alerts ? "#16a34a" : "#cbd5e1", position: "relative", transition: "all .15s", flexShrink: 0 }}>
                <span style={{ position: "absolute", top: 2, left: alerts ? 20 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "all .15s", boxShadow: "0 1px 3px rgba(0,0,0,0.25)" }} />
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }} onClick={() => setAlerts(a => !a)}>
                Enable WhatsApp alerts
              </span>
            </label>

            {msg.text && (
              <div style={{ marginTop: 14, padding: "9px 13px", borderRadius: 9, fontSize: 13, fontWeight: 600,
                background: msg.type === "success" ? "#dcfce7" : "#fee2e2", color: msg.type === "success" ? "#15803d" : "#b91c1c" }}>
                {msg.text}
              </div>
            )}

            <button onClick={save} disabled={saving}
              style={{ marginTop: 18, background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", border: "none", borderRadius: 10, padding: "11px 22px", fontSize: 14, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.65 : 1 }}>
              {saving ? "Saving…" : "Save"}
            </button>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 12 }}>
              Note: WhatsApp delivers engine alerts via an approved Meta template. If you don’t receive a test message after enabling, the template may be pending approval or the access token expired — tell the admin.
            </div>
          </>
        )}
      </div>

      {IS_CRYPTO_PORTAL && <DeltaApiCard />}
    </div>
  );
}
